import { doc, updateDoc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Save student plan metadata to Firestore
 * @param {string} studentId - The student ID
 * @param {Object} meta - The metadata to save
 * @returns {Promise<void>}
 */
export async function saveStudentPlanMeta(studentId, meta) {
  try {
    const studentRef = doc(db, 'students', studentId);
    
    // Get current student data
    const studentSnap = await getDoc(studentRef);
    if (!studentSnap.exists()) {
      throw new Error(`Student with ID ${studentId} not found`);
    }

    // Merge with existing data, preserving other fields
    const currentData = studentSnap.data();
    const updatedData = {
      ...currentData,
      planMeta: {
        ...currentData.planMeta,
        ...meta,
        lastUpdatedAt: new Date().toISOString()
      }
    };

    await updateDoc(studentRef, updatedData);
  } catch (error) {
    console.error('Error saving student plan meta:', error);
    throw error;
  }
}

/**
 * Get student plan metadata from Firestore
 * @param {string} studentId - The student ID
 * @returns {Promise<Object|null>}
 */
export async function getStudentPlanMeta(studentId) {
  try {
    const studentRef = doc(db, 'students', studentId);
    const studentSnap = await getDoc(studentRef);
    
    if (!studentSnap.exists()) {
      return null;
    }

    const studentData = studentSnap.data();
    return studentData.planMeta || null;
  } catch (error) {
    console.error('Error getting student plan meta:', error);
    throw error;
  }
}

/**
 * Log audit event to Firestore
 * @param {string} studentId - The student ID
 * @param {Object} event - The audit event
 * @returns {Promise<void>}
 */
export async function logAuditEvent(studentId, event) {
  try {
    const auditRef = collection(db, 'students', studentId, 'student_audit');
    await addDoc(auditRef, event);
  } catch (error) {
    console.error('Error logging audit event:', error);
    throw error;
  }
}

/**
 * Student Plan Meta interface for TypeScript-like documentation
 * @typedef {Object} StudentPlanMeta
 * @property {string} [curriculumPath] - "NC_WITH_SUPPORTS" | "BESPOKE"
 * @property {number} [decisionScore] - 0–12
 * @property {string} [decisionSource] - "TRIAGE" | "MANUAL_OVERRIDE" | "ADMIN"
 * @property {string} [overrideReason]
 * @property {string} [reviewDate] - ISO date string
 * @property {Array} [triageAnswers] - Array of {q: string, a: string, score: number}
 * @property {string} [lastUpdatedByUid]
 * @property {string} [lastUpdatedAt] - ISO date string
 * @property {string} [ncStatus] - "at_nc" | "nc_below12" | "below_nc" | "not_nc"
 * @property {Object} [questionnaireAnswers] - {ncStatusAnswer: string, branchAnswers: Record<string, string>}
 * @property {Object} [abilityProfile] - Ability profile with derived booleans
 */

/**
 * Ability Profile interface for TypeScript-like documentation
 * @typedef {Object} AbilityProfile
 * @property {string} [expressive_level] - Expressive language level
 * @property {string} [receptive_level] - Receptive language level
 * @property {string} [cognition_level] - Cognition level
 * @property {string} [regulation_independence] - Regulation independence level
 * @property {string} [transitions_tolerance] - Transitions tolerance level
 * @property {string} [group_tolerance] - Group tolerance level
 * @property {boolean} [canUseSymbolicLanguage] - Derived boolean
 * @property {boolean} [canProcessSimpleReflection] - Derived boolean
 * @property {boolean} [canUnderstandTokens] - Derived boolean
 */



